import { CLAUDE_PROXY_URL } from "../config/api";
import { authHeader } from "./apiToken";

const DEFAULT_MAX_TOKENS = 4000;

// Low-level proxy call taking a full messages array (role + content blocks).
async function _fetchClaudeMessages(systemPrompt, messages, maxTokens) {
  const body = {
    system: systemPrompt,
    messages,
    max_tokens: maxTokens || DEFAULT_MAX_TOKENS,
  };

  const res = await fetch(CLAUDE_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Claude proxy error ${res.status}: ${errText}`);
  }

  return res;
}

function _fetchClaude(systemPrompt, userContent, maxTokens) {
  // userContent is already an array of content blocks in most callers
  // (text + document blocks). Wrap in messages format.
  const content = Array.isArray(userContent)
    ? userContent
    : [{ type: "text", text: userContent }];
  return _fetchClaudeMessages(systemPrompt, [{ role: "user", content }], maxTokens);
}

// The proxy returns { text }; fall back to joining raw Anthropic content blocks.
function _extractText(data) {
  return (
    data.text ??
    (data.content || [])
      .filter((b) => b.type === "text")
      .map((b) => b.text)
      .join("")
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// callClaude — awaits full response, returns text string
// The single authoritative proxy call — all AI requests go through cs-est-claude-fn
// ─────────────────────────────────────────────────────────────────────────────
export async function callClaude(systemPrompt, userContent, maxTokens) {
  const startMs = performance.now();

  const res = await _fetchClaude(systemPrompt, userContent, maxTokens);
  const data = await res.json();
  const text = _extractText(data);

  const durationMs = Math.round(performance.now() - startMs);
  console.log(
    `[claudeClient] callClaude completed in ${durationMs}ms ` +
    `input=${data.usage?.input_tokens ?? "?"} ` +
    `output=${data.usage?.output_tokens ?? "?"} ` +
    `stop=${data.stop_reason ?? "?"}`
  );

  return text;
}

// ─────────────────────────────────────────────────────────────────────────────
// callClaudeWithMeta — same as callClaude but returns { text, stopReason }
// Use when the caller needs to detect max_tokens truncation and retry.
// ─────────────────────────────────────────────────────────────────────────────
export async function callClaudeWithMeta(systemPrompt, userContent, maxTokens) {
  const startMs = performance.now();

  const res = await _fetchClaude(systemPrompt, userContent, maxTokens);
  const data = await res.json();
  const text = _extractText(data);
  const stopReason = data.stop_reason ?? null;

  const durationMs = Math.round(performance.now() - startMs);
  console.log(
    `[claudeClient] callClaudeWithMeta completed in ${durationMs}ms ` +
    `input=${data.usage?.input_tokens ?? "?"} ` +
    `output=${data.usage?.output_tokens ?? "?"} ` +
    `stop=${stopReason}`
  );

  return { text, stopReason };
}

// ─────────────────────────────────────────────────────────────────────────────
// callClaudeConversation — multi-turn variant. Pass a full messages array
// (each { role, content }) so a caller can continue a truncated response by
// appending the assistant's partial text and a "continue" user turn. Returns
// { text, stopReason } like callClaudeWithMeta. The proxy is non-streaming, so
// keep max_tokens per call modest and loop on stopReason === "max_tokens"
// rather than requesting one huge response.
// ─────────────────────────────────────────────────────────────────────────────
export async function callClaudeConversation(systemPrompt, messages, maxTokens) {
  const res = await _fetchClaudeMessages(systemPrompt, messages, maxTokens);
  const data = await res.json();
  return { text: _extractText(data), stopReason: data.stop_reason ?? null };
}

